"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import BikeCard from "./rideCard";
import { useSelector, useDispatch } from "react-redux";

import { getSingleVehicleList } from "@/data/staticMethod";
import { usefetchDataFromApi } from "@/utils/api";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import { tokens } from "@/styles/tokens";
import { trackSearch, trackViewItemList, toGa4Item } from "@/utils/analytics";
import { formatDuration } from "@/utils/formatDuration";
import { getRentalDayBilling } from "@/components/ui/RentalDayNote";
import { markSearchIntent } from "@/utils/leadIntent";
import Rupee from "@/components/ui/Rupee";
import Button from "@/components/ui/Button";

// Duration change toast — desktop (top-right) & mobile (bottom)
const DurationToast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  if (typeof window === "undefined") return null;

  return createPortal(
    <>
      {/* Desktop — top right */}
      <div className="fixed z-[9999] right-4 top-20 hidden lg:flex items-center gap-2.5 bg-gray-900 text-white text-sm pl-3 pr-4 py-3 rounded-xl shadow-2xl animate-slide-in-right max-w-sm">
        <div className="bg-primary/20 text-primary rounded-full p-1.5 shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/></svg>
        </div>
        <span className="leading-snug">{message}</span>
        <button onClick={onClose} className="ml-1 text-white/50 hover:text-white text-lg leading-none">×</button>
      </div>
      {/* Mobile — bottom */}
      <div className="fixed z-[9999] bottom-4 left-3 right-3 lg:hidden flex items-center justify-center gap-2 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-2xl animate-slide-in-up">
        <svg className="text-primary" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/></svg>
        <span>{message}</span>
      </div>
    </>,
    document.body
  );
};


const IconClose = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="M6 6l12 12" /></svg>
);
const IconMinus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /></svg>
);
const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
);
const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke={tokens.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);
const IconStar = () => (
  <svg className="w-4 h-4 text-orange-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFA500" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
);
const Spinner = () => <span className="inline-block w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;

const Modal = ({
  isModalOpen,
  modalData,
  handleAddToCart,
  closeModal = () => {},
  loadingKey = null,
  cartError = null,
}) => {
  const searchState = useSelector((state) => state.search);
  const cItemNum = (item) => {
    const details = item?.cartItemList?.details;
    if (!details) return 0;
    return details[item.key] ?? 0;
  };

  const getLocationsInCart = () => {
    if (!modalData?.otherlocation) {
      return [{ name: modalData?.location?.name, total: modalData?.rent?.total, key: modalData?.key, partialStatus: modalData?.vehicle?.partialStatus, cartItemList: modalData?.cartItemList, calculationType: modalData?.rent?.calculationType, rate: modalData?.rent?.rate }];
    }
    return modalData.otherlocation.filter((loc) => {
      const details = loc?.cartItemList?.details;
      return details && Object.keys(details).includes(loc.key);
    });
  };

  const getLocationsNotInCart = () => {
    if (!modalData?.otherlocation) return [];
    return modalData.otherlocation.filter((loc) => {
      const details = loc?.cartItemList?.details;
      return details && !Object.keys(details).includes(loc.key);
    });
  };

  const renderLocationCard = (loc, isInCart) => {
    const qty = cItemNum(loc);
    const isLoading = loadingKey === loc.key;
    const { startDateTime: sdt, endDateTime: edt } = searchState || {};
    const billing = sdt?.date && edt?.date ? getRentalDayBilling(`${sdt.date} ${sdt.time}`, `${edt.date} ${edt.time}`, loc) : null;

    return (
      <React.Fragment key={loc.key}>
      <div className="mb-1.5 px-2.5 py-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2">
        {loc.partialStatus == 1 && (
          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 border border-emerald-500 bg-emerald-50 [&_svg]:w-3 [&_svg]:h-3">
            <IconCheck />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-gray-900 leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis">{loc.name}</div>
          <div className="text-[12px] font-medium text-gray-500 leading-[1.2]">@{loc.total?.toFixed(2)}</div>
          {billing && (
            <div className="text-[11px] text-gray-500 leading-[1.3] mt-0.5">{billing.days} rental days · day ends {billing.resetAt}</div>
          )}
        </div>

        {isInCart ? (
          <div className="flex items-center gap-1 shrink-0">
            <button className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center cursor-pointer transition-colors p-0 hover:enabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:w-4 [&_svg]:h-4 [&_svg]:text-gray-900" disabled={isLoading} onClick={() => handleAddToCart(modalData, loc.key, "remove")}>
              {isLoading ? <Spinner /> : <IconMinus />}
            </button>
            <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[13px] font-semibold text-gray-900">
              {isLoading ? <Spinner /> : qty}
            </div>
            <button className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center cursor-pointer transition-colors p-0 hover:enabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:w-4 [&_svg]:h-4 [&_svg]:text-gray-900" disabled={isLoading} onClick={() => handleAddToCart(modalData, loc.key, "add")}>
              {isLoading ? <Spinner /> : <IconPlus />}
            </button>
          </div>
        ) : (
          <button className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center cursor-pointer transition-colors p-0 hover:enabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:w-4 [&_svg]:h-4 [&_svg]:text-gray-900" disabled={isLoading} onClick={() => handleAddToCart(modalData, loc.key, "add")}>
            {isLoading ? <Spinner /> : <IconPlus />}
          </button>
        )}
      </div>
      {cartError?.key === loc.key && (
        <p role="alert" className="mb-1.5 px-1 text-xs font-medium text-red-600">{cartError.message}</p>
      )}
      </React.Fragment>
    );
  };

  if (!isModalOpen) return null;

  const locationsInCart = getLocationsInCart();
  const locationsNotInCart = getLocationsNotInCart();
  const anyRentalDayBilling = [...locationsInCart, ...locationsNotInCart].some((loc) =>
    getRentalDayBilling(`${searchState?.startDateTime?.date} ${searchState?.startDateTime?.time}`, `${searchState?.endDateTime?.date} ${searchState?.endDateTime?.time}`, loc)
  );
  const specs = [modalData?.vehicle?.transmissionType, modalData?.vehicle?.fuelType].filter(Boolean).join(" • ");

  // Subtotal for this vehicle's added locations, so the user sees what they'll pay
  // before tapping Proceed to Checkout.
  const cartQty = locationsInCart.reduce((s, loc) => s + (cItemNum(loc) || 0), 0);
  const cartSubtotal = locationsInCart.reduce((s, loc) => s + (Number(loc.total) || 0) * (cItemNum(loc) || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-end lg:items-center justify-center bg-black/50" onClick={closeModal}>
      <div className="relative bg-white w-full max-w-[440px] h-[85vh] lg:h-auto lg:max-h-[75vh] rounded-t-2xl lg:rounded-xl flex flex-col overflow-hidden animate-mc-slide-up lg:animate-mc-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mt-1.5 mb-1 shrink-0 lg:hidden" />

        <div className="px-4 pt-2 pb-2.5 border-b border-gray-200 shrink-0">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-base font-bold text-gray-900 leading-[1.15] tracking-tight m-0">
              {modalData?.vehicle?.company} {modalData?.vehicle?.name}
            </h2>
            <button className="w-8 h-8 flex items-center justify-center bg-transparent border-none cursor-pointer shrink-0 rounded-full transition-colors hover:bg-gray-100 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:text-gray-900" onClick={closeModal}>
              <IconClose />
            </button>
          </div>

          <div className="flex items-start gap-3">
            <Image
              className="w-[100px] h-[72px] object-contain shrink-0 rounded-md"
              src={modalData?.vehicle?.imageUrl || '/images/img_notfound.png'}
              alt={`${modalData?.vehicle?.company} ${modalData?.vehicle?.name}`}
              width={160}
              height={120}
              unoptimized
              style={{ width: '100px', height: '72px' }}
              onError={(e) => { e.target.onerror = null; e.target.src = "/images/img_notfound.png"; }}
            />
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
              {specs && <span className="text-[12px] text-gray-500 font-normal leading-[1.3] capitalize">{specs}</span>}
              {modalData?.totalDayTime && (
                <span className="text-xs text-gray-500 font-normal leading-[1.3]">
                  {anyRentalDayBilling ? "Prices for your dates" : `Prices for ${formatDuration(modalData.totalDayTime)}`}
                </span>
              )}
              <span className="text-xs text-gray-500 font-normal leading-[1.3]">Prices are inclusive of all the taxes</span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pt-3 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-black/10 [&::-webkit-scrollbar-thumb]:rounded-full">
          {locationsInCart.length > 0 && (
            <>
              <p className="text-[12px] font-semibold text-gray-500 leading-[1.2] tracking-wider mb-2">You Added From</p>
              {locationsInCart.map((loc) => renderLocationCard(loc, true))}
            </>
          )}

          <div className="h-3" />

          {locationsNotInCart.length > 0 && (
            <>
              <p className="text-[12px] font-semibold text-gray-500 leading-[1.2] tracking-wider mb-2">Also available at these locations</p>
              {locationsNotInCart.map((loc) => renderLocationCard(loc, false))}
            </>
          )}

          <div className="h-3" />
        </div>

        <div className="px-3 pt-2.5 pb-3 border-t border-gray-200 bg-white shrink-0">
          {cartQty > 0 && (
            <div className="flex items-center justify-between mb-2.5 px-0.5">
              <span className="text-[13px] text-gray-500">This vehicle · {cartQty} {cartQty === 1 ? 'item' : 'items'}</span>
              <span className="text-base font-bold text-gray-900"><Rupee />{cartSubtotal.toFixed(2)}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" size="md" className="flex-1 min-w-0" onClick={closeModal}>
              Add More
            </Button>
            <Button href="/cart" size="md" className="flex-1 min-w-0">
              Checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RidesSection = () => {
  const {
    searchVehicles,
    startDateTime,
    endDateTime,
    city,
    vehicleType,
    searchType,
  } = useSelector((state) => state.search);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [newVehicle, setNewVehicles] = useState(searchVehicles);
  const sDateTime = `${startDateTime.date} ${startDateTime.time}`;
  const eDateTime = `${endDateTime.date} ${endDateTime.time}`;
  const [loadingKey, setLoadingKey] = useState(null);
  const [cartError, setCartError] = useState(null);
  const [durationToast, setDurationToast] = useState(null);
  const clearDurationToast = useCallback(() => setDurationToast(null), []);
  const handleDurationToast = useCallback((date, time) => {
    setDurationToast(`End date changed to ${date}, ${time}`);
  }, []);

  const openModal = (data) => {
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;
    let finalVehicleList = getSingleVehicleList(data, startDTime, endDTime);
    setModalData(finalVehicleList[0]);
    setIsModalOpen(true);
  };

  useEffect(() => {
    setNewVehicles(searchVehicles);
  }, [searchVehicles]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleAddToCart = (item, itemkey, action) => {
    setLoadingKey(itemkey);
    setCartError(null);
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;
    usefetchDataFromApi({
      url: "cart/update",
      method: "POST",
      data: {
        key: itemkey,
        search: {
          types: vehicleType,
          start: startDTime,
          end: endDTime,
          cityId: city.id,
          longRentStatus: searchType == "/monthly-bike-rental" ? 1 : 0,
          platform: 3,
        },
        platform: 3,
        action: action,
      },
    }).then((res) => {
      if (res.success) {
        dispatch(bumpCartVersion());
        handleMiniCart(item?.vehicle?.id, itemkey);
      } else {
        setCartError({ key: itemkey, message: res.message || "Could not update the cart. Please try again." });
      }
      setLoadingKey(null);
    }).catch(() => {
      setLoadingKey(null);
    });
  };

  const handleMiniCart = (vehicleId, key) => {
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;
    usefetchDataFromApi({
      url: "cart/minicart",
      method: "POST",

      data: {
        key: key,
        types: vehicleType,
        search: {
          types: vehicleType,
          cityId: city.id,
          start: startDTime,
          end: endDTime,
          longRentStatus: searchType == "/monthly-bike-rental" ? 1 : 0,
          platform: 3,
        },
        vehicleId: vehicleId,
        cityId: city.id,
        platform: 3,
      },
    }).then((res) => {
      let finalVehicleList = getSingleVehicleList(
        res.data?.data,
        startDTime,
        endDTime
      );
      setModalData(finalVehicleList[0]);
    });
  };

  const isSearching = useSelector((state) => state.search.isSearching);
  const showSkeleton = isSearching || (newVehicle.length === 0 && !isSearching && typeof window !== "undefined" && !window.__sukutoSearchDone);

  useEffect(() => {
    if (!isSearching && newVehicle.length > 0) {
      window.__sukutoSearchDone = true;
    }
    if (isSearching) {
      window.__sukutoSearchDone = false;
    }
  }, [isSearching, newVehicle.length]);

  // Fire `search` + `view_item_list` once per completed search (when loading
  // finishes). Resets when the next search starts so each result set is
  // tracked exactly once.
  const searchTrackedRef = useRef(false);
  useEffect(() => {
    if (isSearching) {
      searchTrackedRef.current = false;
      return;
    }
    if (searchTrackedRef.current) return;
    searchTrackedRef.current = true;

    const searchFacts = {
      city: city?.name,
      startDate: startDateTime?.date,
      endDate: endDateTime?.date,
      startTime: startDateTime?.time,
      endTime: endDateTime?.time,
      vehicleType,
      resultCount: searchVehicles?.length || 0,
    };

    trackSearch(searchFacts);
    markSearchIntent(searchFacts);

    if (searchVehicles && searchVehicles.length > 0) {
      const items = searchVehicles.map((bike, i) =>
        toGa4Item(bike, bike?.rent?.total, i)
      );
      trackViewItemList(items, "Search Results");
    }
  }, [isSearching, searchVehicles, city, startDateTime, endDateTime, vehicleType]);

  return (
    <>
      <div className="mx-auto mt-3 lg:mt-4 px-[4%]">
        <Modal
          closeModal={closeModal}
          isModalOpen={isModalOpen}
          modalData={modalData}
          handleAddToCart={handleAddToCart}
          loadingKey={loadingKey}
          cartError={cartError}
        />

        {showSkeleton ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-7 mb-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100">
                <div className="w-full h-[180px] bg-gray-100 animate-skeleton-pulse" />
                <div className="p-3.5 flex flex-col gap-2.5">
                  <div className="h-3.5 rounded-md bg-gray-100 animate-skeleton-pulse" style={{ width: "70%" }} />
                  <div className="h-3.5 rounded-md bg-gray-100 animate-skeleton-pulse" style={{ width: "50%" }} />
                  <div className="h-3 rounded-md bg-gray-100 animate-skeleton-pulse" style={{ width: "35%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : newVehicle.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-7 mb-6">
            {newVehicle.map((bike) => (
              <BikeCard
                openModal={openModal}
                key={bike.id}
                cardData={bike}
                sDateTime={sDateTime}
                eDateTime={eDateTime}
                onDurationChange={handleDurationToast}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-[60px] px-5 text-center">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="#D1D5DB" strokeWidth="2"/>
              <path d="m21 21-4.35-4.35" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h3 className="text-lg font-semibold text-textPrimary mt-4 mb-1.5">No vehicles found</h3>
            <p className="text-sm text-gray-400 m-0">Try adjusting your dates, location, or vehicle type.</p>
          </div>
        )}
      </div>

      {/* Duration change toast */}
      {durationToast && (
        <DurationToast message={durationToast} onClose={clearDurationToast} />
      )}
    </>
  );
};

export default RidesSection;
